/**
 * dataSchemas.ts
 *
 * Runtime data validation and normalization for reservation objects.
 * Ensures data integrity at API boundaries and service layers.
 */

interface ReservationDetail {
    [key: string]: unknown;
}

interface Reservation {
    id: string;
    detail: ReservationDetail;
    customer_name?: string;
    start_date?: string;
    end_date?: string;
    status?: string | number;
    [key: string]: unknown;
}

interface Vehicle {
    id: string;
    vehicle_code?: string;
    vehicle_name?: string;
    [key: string]: unknown;
}

/**
 * Validates and normalizes a reservation object.
 * Guarantees that detail is always an object (never null/undefined).
 *
 * @param reservation - Raw reservation data from API
 * @returns Normalized reservation with guaranteed structure
 */
export function validateReservation(reservation: unknown): Reservation {
    if (!reservation || typeof reservation !== 'object') {
        console.error('[dataSchemas] Invalid reservation data:', reservation);
        return {
            id: '',
            detail: {},
            customer_name: '',
            start_date: '',
            end_date: ''
        };
    }

    const raw = reservation as Record<string, unknown>;

    // Normalize detail - ALWAYS an object
    const normalizedDetail = raw.detail ?? {};

    // Ensure detail is actually an object (not array, not primitive)
    if (typeof normalizedDetail !== 'object' || Array.isArray(normalizedDetail)) {
        console.warn('[dataSchemas] reservation.detail is not an object:', {
            id: raw.id,
            detailType: typeof normalizedDetail,
            isArray: Array.isArray(normalizedDetail)
        });
    }

    return {
        ...raw,
        id: raw.id as string,
        detail: typeof normalizedDetail === 'object' && !Array.isArray(normalizedDetail)
            ? normalizedDetail as ReservationDetail
            : {}
    } as Reservation;
}

/**
 * Validates an array of reservations.
 *
 * @param reservations - Array of reservation objects
 * @returns Normalized reservations array
 */
export function validateReservations(reservations: unknown): Reservation[] {
    if (!Array.isArray(reservations)) {
        console.error('[dataSchemas] Expected array of reservations, got:', typeof reservations);
        return [];
    }

    return reservations.map(validateReservation);
}

/**
 * Validates and normalizes a vehicle object.
 *
 * @param vehicle - Raw vehicle data
 * @returns Normalized vehicle
 */
export function validateVehicle(vehicle: unknown): Vehicle {
    if (!vehicle || typeof vehicle !== 'object') {
        console.error('[dataSchemas] Invalid vehicle data:', vehicle);
        return {
            id: '',
            vehicle_code: '',
            vehicle_name: ''
        };
    }

    return {
        ...(vehicle as Record<string, unknown>)
    } as Vehicle;
}

/**
 * Validates an array of vehicles.
 *
 * @param vehicles - Array of vehicle objects
 * @returns Normalized vehicles array
 */
export function validateVehicles(vehicles: unknown): Vehicle[] {
    if (!Array.isArray(vehicles)) {
        console.error('[dataSchemas] Expected array of vehicles, got:', typeof vehicles);
        return [];
    }

    return vehicles.map(validateVehicle);
}

/**
 * Type guards and helpers
 */

/**
 * Checks if a value is a valid reservation object.
 *
 * @param obj - Value to check
 * @returns True if valid reservation
 */
export function isValidReservation(obj: unknown): obj is Reservation {
    return (
        obj != null &&
        typeof obj === 'object' &&
        !Array.isArray(obj) &&
        typeof (obj as Record<string, unknown>).id === 'string' &&
        typeof (obj as Record<string, unknown>).detail === 'object' &&
        !Array.isArray((obj as Record<string, unknown>).detail)
    );
}

/**
 * Normalizes detail object - ensures it's always a plain object.
 *
 * @param detail - Detail value to normalize
 * @returns Normalized detail object
 */
export function normalizeDetail(detail: unknown): ReservationDetail {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) {
        return {};
    }
    return detail as ReservationDetail;
}

/**
 * Safely gets a property from detail with fallback.
 * This is a utility for gradual migration.
 *
 * @param reservation - Reservation object
 * @param property - Property name to get from detail
 * @param defaultValue - Default value if not found
 * @returns Property value or default
 */
export function getDetailProperty(reservation: Reservation | null | undefined, property: string, defaultValue: unknown = ''): unknown {
    if (!reservation || !reservation.detail) {
        return defaultValue;
    }

    const value = reservation.detail[property];
    return value !== undefined && value !== null ? value : defaultValue;
}
